import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '@iconify/react';
import { Button as AntButton, Input, Modal, Progress } from 'antd';
import { Button } from '../../components/ui/Button';
import { useTranslation } from '../../contexts/LanguageContext';
import { useSelectedAccount } from '../../contexts/SelectedAccountContext';
import { useTheme } from '../../contexts/ThemeContext';
import { AccountDropdown, SkeletonLine, SkeletonListItem, useToast } from '../../components/ui';
import MainLayout from '../../layouts/MainLayout';
import apiClient from '../../services/api/apiClient';
import styles from '../home-insights/DashboardInsights.module.css';

const { TextArea } = Input;

// Debounce helper
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

function PostsCommentsPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { selectedAccountId, setSelectedAccountId, accounts } = useSelectedAccount();
  const { toast, confirm: toastConfirm } = useToast();

  const [posts, setPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [displayedPostsCount, setDisplayedPostsCount] = useState(20);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [allReplies, setAllReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [displayedRepliesCount, setDisplayedRepliesCount] = useState(20);
  const [currentUsername, setCurrentUsername] = useState(null);

  // Reply input
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [loadingAiSuggestion, setLoadingAiSuggestion] = useState(null);

  // Batch reply - Background Job System
  const [batchReplying, setBatchReplying] = useState(false);
  const [batchPaused, setBatchPaused] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [batchSuggestions, setBatchSuggestions] = useState({});
  const [batchSending, setBatchSending] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollingIntervalRef = useRef(null);
  const batchCancelledRef = useRef(false);
  const batchPausedRef = useRef(false);

  // Unified reply
  const [showUnifiedReplyModal, setShowUnifiedReplyModal] = useState(false);
  const [unifiedReplyText, setUnifiedReplyText] = useState('');
  const [unifiedReplyFilter, setUnifiedReplyFilter] = useState('');
  const [sendingUnifiedReply, setSendingUnifiedReply] = useState(false);
  const [unifiedReplyProgress, setUnifiedReplyProgress] = useState({ current: 0, total: 0 });

  // Custom prompt for batch AI reply
  const [showBatchPromptModal, setShowBatchPromptModal] = useState(false);
  const [batchCustomPrompt, setBatchCustomPrompt] = useState('');

  // Draft ID mapping (reply_id -> draft_id) for saving edits
  const [draftIdMap, setDraftIdMap] = useState({});
  const saveDraftTimeoutRef = useRef({});

  // Post content expand/collapse
  const [isPostContentExpanded, setIsPostContentExpanded] = useState(false);

  // Filter only active Threads accounts with valid token for the dropdown
  const threadsAccounts = accounts.filter(acc =>
    acc.platform === 'threads' &&
    acc.status === 'active' &&
    acc.access_token
  );

  // Debug: 檢查帳號過濾
  console.log('🔍 All accounts:', accounts.map(a => ({ name: a.account_name, status: a.status, hasToken: !!a.access_token })));
  console.log('✅ Filtered threadsAccounts:', threadsAccounts.length);

  // Load posts when selected account changes
  useEffect(() => {
    if (selectedAccountId && accounts.length > 0) {
      const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
      if (selectedAccount && selectedAccount.platform === 'threads') {
        loadRecentPosts();
        checkActiveJobs(); // Check for any ongoing background jobs
      }
    }
  }, [selectedAccountId, accounts]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Check for active background jobs
  const checkActiveJobs = async () => {
    try {
      const response = await apiClient.get('/reply-jobs/user/active');
      if (response.data.success && response.data.data.jobs.length > 0) {
        const activeJob = response.data.data.jobs[0]; // Get most recent active job
        setCurrentJobId(activeJob.id);
        setJobStatus(activeJob.status);

        if (activeJob.status === 'processing' || activeJob.status === 'pending') {
          setBatchReplying(true);
          setBatchPaused(activeJob.status === 'paused');
          startPolling(activeJob.id);
        }
      }
    } catch (err) {
      console.error('Failed to check active jobs:', err);
    }
  };

  // Start polling for job status
  const startPolling = (jobId) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await apiClient.get(`/reply-jobs/${jobId}`);
        if (response.data.success) {
          const { job, drafts, progress } = response.data.data;

          setJobStatus(job.status);
          setBatchProgress({
            current: progress.processed,
            total: progress.total,
            percentage: progress.percentage
          });

          // Update suggestions and draft ID map from drafts
          const newSuggestions = {};
          const newDraftIdMap = {};
          drafts.forEach(draft => {
            if (draft.ai_suggestion || draft.edited_text) {
              newSuggestions[draft.reply_id] = draft.edited_text || draft.ai_suggestion;
              newDraftIdMap[draft.reply_id] = draft.id;
            }
          });
          setBatchSuggestions(prev => ({ ...prev, ...newSuggestions }));
          setDraftIdMap(prev => ({ ...prev, ...newDraftIdMap }));

          // Check if job is completed or failed
          if (job.status === 'completed') {
            stopPolling();
            setBatchReplying(false);
            toast.success(`Successfully generated ${progress.processed} AI reply suggestions`);
          } else if (job.status === 'failed') {
            stopPolling();
            setBatchReplying(false);
            toast.error(job.error_message || 'Job failed');
          } else if (job.status === 'paused') {
            setBatchPaused(true);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000); // Poll every 2 seconds
  };

  // Stop polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Load drafts for current post
  const loadDraftsForPost = async (postId) => {
    try {
      const response = await apiClient.get(`/reply-jobs/drafts/${postId}`);
      if (response.data.success && response.data.data.drafts) {
        const newSuggestions = {};
        const newDraftIdMap = {};
        response.data.data.drafts.forEach(draft => {
          if (draft.ai_suggestion || draft.edited_text) {
            newSuggestions[draft.reply_id] = draft.edited_text || draft.ai_suggestion;
            newDraftIdMap[draft.reply_id] = draft.id; // Map reply_id to draft_id
          }
        });
        setBatchSuggestions(prev => ({ ...prev, ...newSuggestions }));
        setDraftIdMap(prev => ({ ...prev, ...newDraftIdMap }));
      }

      // Check for any active jobs and resume polling if needed
      await checkAndResumeActiveJobs();
    } catch (err) {
      console.error('Failed to load drafts:', err);
    }
  };

  // Check for active jobs and resume polling if needed
  const checkAndResumeActiveJobs = async () => {
    try {
      const response = await apiClient.get('/reply-jobs/user/active');
      if (response.data.success && response.data.data.jobs?.length > 0) {
        const activeJob = response.data.data.jobs[0]; // Get the most recent active job

        console.log('Found active job:', activeJob.id, 'status:', activeJob.status);

        // Resume state based on job status
        setCurrentJobId(activeJob.id);
        setJobStatus(activeJob.status);
        setBatchReplying(activeJob.status !== 'completed' && activeJob.status !== 'failed');
        setBatchPaused(activeJob.status === 'paused');
        setBatchProgress({
          current: activeJob.processed_items || 0,
          total: activeJob.total_items || 0,
          percentage: activeJob.total_items > 0
            ? Math.round((activeJob.processed_items / activeJob.total_items) * 100)
            : 0
        });

        // Start polling if job is still in progress
        if (['pending', 'processing'].includes(activeJob.status)) {
          console.log('Resuming polling for active job:', activeJob.id);
          startPolling(activeJob.id);
        }
      }
    } catch (err) {
      console.error('Failed to check active jobs:', err);
    }
  };

  // Save edited draft to Supabase (debounced)
  const saveDraftEdit = useCallback(async (replyId, editedText) => {
    const draftId = draftIdMap[replyId];
    if (!draftId) {
      console.warn('No draft ID found for reply:', replyId);
      return;
    }

    try {
      await apiClient.put(`/reply-jobs/drafts/${draftId}`, {
        editedText
      });
      console.log('Draft saved successfully:', draftId);
    } catch (err) {
      console.error('Failed to save draft edit:', err);
    }
  }, [draftIdMap]);

  // Handle suggestion edit with debounced save
  const handleSuggestionEdit = useCallback((replyId, newText) => {
    // Update local state immediately
    setBatchSuggestions(prev => ({
      ...prev,
      [replyId]: newText
    }));

    // Clear existing timeout for this reply
    if (saveDraftTimeoutRef.current[replyId]) {
      clearTimeout(saveDraftTimeoutRef.current[replyId]);
    }

    // Set new debounced save (1 second delay)
    saveDraftTimeoutRef.current[replyId] = setTimeout(() => {
      saveDraftEdit(replyId, newText);
    }, 1000);
  }, [saveDraftEdit]);

  // Load posts (from cache)
  const loadRecentPosts = async () => {
    if (!selectedAccountId) return;

    try {
      setLoadingPosts(true);
      setDisplayedPostsCount(20); // Reset displayed count
      const response = await apiClient.get(`/threads/insights/cached-posts?accountId=${selectedAccountId}`);

      if (response.data.success && response.data.data) {
        const { posts: cachedPosts, accountName } = response.data.data;
        setCurrentUsername(accountName);

        const threadsPosts = cachedPosts
          .filter(post => post.media_type !== 'REPOST_FACADE')
          .map(post => ({
            id: post.post_id,
            content: post.content_text || 'No content',
            createdAt: post.post_timestamp,
            likeCount: post.likes_count ?? post.like_count ?? null,
            replyCount: post.replies_count ?? post.reply_count ?? null,
            repostCount: post.reposts_count ?? post.repost_count ?? null,
            quoteCount: post.quotes_count ?? post.quote_count ?? null
          }));

        setAllPosts(threadsPosts);
        setPosts(threadsPosts.slice(0, 20));
      }
    } catch (err) {
      console.error('Failed to load posts:', err);
      setAllPosts([]);
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  // Load all posts from Supabase
  const loadAllPosts = async () => {
    if (!selectedAccountId) return;

    try {
      setLoadingMorePosts(true);
      // Load all posts without limit
      const response = await apiClient.get(`/threads/insights/cached-posts?accountId=${selectedAccountId}&limit=1000`);

      if (response.data.success && response.data.data) {
        const { posts: cachedPosts, accountName } = response.data.data;
        setCurrentUsername(accountName);

        const threadsPosts = cachedPosts
          .filter(post => post.media_type !== 'REPOST_FACADE')
          .map(post => ({
            id: post.post_id,
            content: post.content_text || 'No content',
            createdAt: post.post_timestamp,
            likeCount: post.likes_count ?? post.like_count ?? null,
            replyCount: post.replies_count ?? post.reply_count ?? null,
            repostCount: post.reposts_count ?? post.repost_count ?? null,
            quoteCount: post.quotes_count ?? post.quote_count ?? null
          }));

        setAllPosts(threadsPosts);
        setPosts(threadsPosts);
        setDisplayedPostsCount(threadsPosts.length);
      }
    } catch (err) {
      console.error('Failed to load all posts:', err);
    } finally {
      setLoadingMorePosts(false);
    }
  };

  // Refresh posts (force fetch from Threads API)
  const [refreshing, setRefreshing] = useState(false);
  const refreshPosts = async () => {
    if (!selectedAccountId) return;

    try {
      setRefreshing(true);
      // Call sync-posts API to force refresh from Threads
      const syncResponse = await apiClient.post('/threads/insights/sync-posts', {
        accountId: selectedAccountId
      });

      if (syncResponse.data.success) {
        toast.success('Posts refreshed from Threads');
        // Reload posts after sync
        await loadRecentPosts();
      } else {
        toast.error(syncResponse.data.error || 'Failed to refresh posts');
      }
    } catch (err) {
      console.error('Failed to refresh posts:', err);
      toast.error('Failed to refresh posts from Threads');
    } finally {
      setRefreshing(false);
    }
  };

  // Handle post click to load replies
  const handlePostClick = async (post) => {
    if (selectedPost && selectedPost.id === post.id) {
      setSelectedPost(null);
      setReplies([]);
      setAllReplies([]);
      setIsPostContentExpanded(false);
      return;
    }

    setSelectedPost(post);
    setIsPostContentExpanded(false);
    setLoadingReplies(true);
    setReplyingToId(null);
    setReplyText('');
    setBatchSuggestions({});

    try {
      const response = await apiClient.get(`/threads/insights/post-replies/${post.id}?accountId=${selectedAccountId}`);

      if (response.data.success && response.data.data) {
        // API 返回的是 { replies: [...], paging: {...} }
        const repliesArray = response.data.data.replies || [];
        setAllReplies(repliesArray);
        setReplies(repliesArray.slice(0, displayedRepliesCount));

        // Load any saved drafts for this post
        await loadDraftsForPost(post.id);
      }
    } catch (err) {
      console.error('Failed to load replies:', err);
      toast.error('Failed to load comments');
    } finally {
      setLoadingReplies(false);
    }
  };

  // Load all replies
  const handleLoadAllReplies = () => {
    setReplies(allReplies);
    setDisplayedRepliesCount(allReplies.length);
  };

  // Send single reply
  const handleSendReply = async (replyToId) => {
    if (!replyText.trim()) return;

    setSendingReply(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/threads/insights/send-reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accountId: selectedAccountId,
          replyToId: replyToId,
          text: replyText
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Reply sent successfully!');
        handlePostClick(selectedPost);
        setReplyText('');
        setReplyingToId(null);
      } else {
        toast.error(data.message || 'Failed to send reply');
      }
    } catch (err) {
      console.error('Failed to send reply:', err);
      toast.error('Error sending reply');
    } finally {
      setSendingReply(false);
    }
  };

  // AI suggestion for single reply
  const handleAiSuggestion = async (reply) => {
    setReplyingToId(reply.id);
    setReplyText('');
    setLoadingAiSuggestion(reply.id);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/threads/insights/suggest-reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          postContent: selectedPost.content,
          replyText: reply.text,
          replyUsername: reply.username
        })
      });

      const data = await response.json();

      if (data.success && data.data.suggestion) {
        setReplyText(data.data.suggestion);
      } else {
        toast.error('Failed to get AI suggestion');
        setReplyingToId(null);
      }
    } catch (err) {
      console.error('Failed to get AI suggestion:', err);
      toast.error('Error getting AI suggestion');
      setReplyingToId(null);
    } finally {
      setLoadingAiSuggestion(null);
    }
  };

  // Open batch prompt modal
  const openBatchPromptModal = () => {
    if (!selectedPost || replies.length === 0) return;

    const filteredReplies = replies.filter(reply => reply.username !== currentUsername && !reply.replied_by_me);

    if (filteredReplies.length === 0) {
      toast.warning('No comments to reply to');
      return;
    }

    setShowBatchPromptModal(true);
  };

  // Batch AI reply with custom prompt - Using Background Job API
  const handleBatchAiReply = async (customPrompt = '') => {
    if (batchReplying) return;
    if (!selectedPost || replies.length === 0) return;

    setShowBatchPromptModal(false);

    const filteredReplies = replies.filter(reply => reply.username !== currentUsername && !reply.replied_by_me);

    if (filteredReplies.length === 0) {
      toast.warning('No comments to reply to');
      return;
    }

    const suggestions = { ...batchSuggestions };
    const repliesToProcess = filteredReplies.filter(reply => !suggestions[reply.id]);

    if (repliesToProcess.length === 0) {
      toast.info('All comments already have AI suggestions');
      return;
    }

    setBatchReplying(true);
    setBatchPaused(false);
    setBatchProgress({ current: 0, total: repliesToProcess.length, percentage: 0 });

    try {
      // Create background job via API
      const response = await apiClient.post('/reply-jobs/generate', {
        accountId: selectedAccountId,
        replies: repliesToProcess.map(reply => ({
          id: reply.id,
          postId: selectedPost.id,
          text: reply.text,
          username: reply.username,
          parentReplyId: reply.parent_id || null
        })),
        customPrompt: customPrompt || undefined,
        postContent: selectedPost.content
      });

      if (response.data.success) {
        const { jobId } = response.data.data;
        setCurrentJobId(jobId);
        setJobStatus('pending');
        toast.info('Background job started. Processing will continue even if you leave this page.');

        // Start polling for updates
        startPolling(jobId);
      } else {
        throw new Error(response.data.error || 'Failed to create job');
      }
    } catch (err) {
      console.error('Failed to create batch AI reply job:', err);
      toast.error('Error starting batch AI reply: ' + (err.message || 'Unknown error'));
      setBatchReplying(false);
      setBatchPaused(false);
      setBatchProgress({ current: 0, total: 0, percentage: 0 });
    }
  };

  // Pause/Resume batch - Using Background Job API
  const handlePauseBatch = async () => {
    if (!currentJobId) return;

    try {
      if (batchPaused) {
        // Resume
        const response = await apiClient.post(`/reply-jobs/${currentJobId}/resume`);
        if (response.data.success) {
          setBatchPaused(false);
          toast.info('Job resumed');
        }
      } else {
        // Pause
        const response = await apiClient.post(`/reply-jobs/${currentJobId}/pause`);
        if (response.data.success) {
          setBatchPaused(true);
          toast.info('Job paused');
        }
      }
    } catch (err) {
      console.error('Failed to pause/resume job:', err);
      toast.error('Failed to pause/resume job');
    }
  };

  // Cancel batch - Using Background Job API
  const handleCancelBatch = async () => {
    if (!currentJobId) {
      setBatchReplying(false);
      setBatchPaused(false);
      setBatchProgress({ current: 0, total: 0, percentage: 0 });
      return;
    }

    try {
      stopPolling();
      const response = await apiClient.delete(`/reply-jobs/${currentJobId}`);
      if (response.data.success) {
        toast.info('Job cancelled');
      }
    } catch (err) {
      console.error('Failed to cancel job:', err);
    } finally {
      setCurrentJobId(null);
      setJobStatus(null);
      setBatchReplying(false);
      setBatchPaused(false);
      setBatchProgress({ current: 0, total: 0, percentage: 0 });
    }
  };

  // Clear all AI suggestions
  const handleClearAllSuggestions = async () => {
    if (Object.keys(batchSuggestions).length === 0) return;

    const confirmed = await toastConfirm({
      title: 'Clear All AI Suggestions',
      message: `Are you sure you want to delete all ${Object.keys(batchSuggestions).length} AI suggestions? This will also remove them from the database.`,
      confirmText: 'Delete All',
      cancelText: 'Cancel',
      variant: 'destructive'
    });

    if (!confirmed) return;

    try {
      // Stop polling first
      stopPolling();

      // Delete from Supabase (with accountId filter if available)
      const params = new URLSearchParams();
      if (selectedAccountId) {
        params.append('accountId', selectedAccountId);
      }

      await apiClient.delete(`/reply-jobs/drafts/clear-all?${params.toString()}`);

      // Clear local state
      setBatchSuggestions({});
      setDraftIdMap({});
      setCurrentJobId(null);
      setJobStatus(null);
      setBatchReplying(false);
      setBatchPaused(false);
      setBatchProgress({ current: 0, total: 0, percentage: 0 });
      toast.success('All AI suggestions cleared');
    } catch (err) {
      console.error('Failed to clear suggestions:', err);
      toast.error('Failed to clear AI suggestions');
    }
  };

  // Batch send all replies
  const handleBatchSend = async () => {
    if (Object.keys(batchSuggestions).length === 0) {
      toast.warning('No replies to send');
      return;
    }

    setBatchSending(true);
    const token = localStorage.getItem('token');
    let sentCount = 0;
    const totalCount = Object.keys(batchSuggestions).length;

    try {
      for (const [replyId, suggestion] of Object.entries(batchSuggestions)) {
        try {
          const response = await fetch(`/api/threads/insights/send-reply`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              accountId: selectedAccountId,
              replyToId: replyId,
              text: suggestion
            })
          });

          const data = await response.json();
          if (data.success) {
            sentCount++;
          }

          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (err) {
          console.error(`Failed to send reply to ${replyId}:`, err);
        }
      }

      toast.success(`Successfully sent ${sentCount} / ${totalCount} replies`);
      handlePostClick(selectedPost);
      setBatchSuggestions({});
    } catch (err) {
      console.error('Failed to batch send replies:', err);
      toast.error('Error sending batch replies');
    } finally {
      setBatchSending(false);
    }
  };

  // Unified reply - now runs in background
  const handleUnifiedReply = async () => {
    if (!unifiedReplyText.trim()) {
      toast.warning('Please enter reply content');
      return;
    }

    // Filter replies: exclude own replies and already replied
    let filteredReplies = replies.filter(reply => reply.username !== currentUsername && !reply.replied_by_me);

    // If filter keyword is provided, only include replies containing the keyword
    if (unifiedReplyFilter.trim()) {
      const filterKeyword = unifiedReplyFilter.trim().toLowerCase();
      filteredReplies = filteredReplies.filter(reply =>
        reply.text && reply.text.toLowerCase().includes(filterKeyword)
      );
    }

    if (filteredReplies.length === 0) {
      toast.warning(unifiedReplyFilter.trim()
        ? `No comments containing "${unifiedReplyFilter.trim()}" found`
        : 'No comments to reply to'
      );
      return;
    }

    const filterInfo = unifiedReplyFilter.trim()
      ? ` (filtered by "${unifiedReplyFilter.trim()}")`
      : '';
    const confirmed = await toastConfirm({
      title: 'Unified Reply',
      message: `Are you sure you want to reply to ${filteredReplies.length} comments${filterInfo} with the same content? This will run in the background - you can close this modal and continue browsing.`,
      confirmText: 'Send Replies',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    setSendingUnifiedReply(true);
    const token = localStorage.getItem('token');

    try {
      // Create background job for unified send
      const response = await fetch(`/api/reply-jobs/unified-send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accountId: selectedAccountId,
          replyIds: filteredReplies.map(r => r.id),
          text: unifiedReplyText.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Unified reply job started! Sending to ${filteredReplies.length} comments${filterInfo} in the background.`);
        setShowUnifiedReplyModal(false);
        setUnifiedReplyText('');
        setUnifiedReplyFilter('');

        // Set job ID for progress tracking if needed
        if (data.data?.jobId) {
          setCurrentJobId(data.data.jobId);
        }
      } else {
        throw new Error(data.error || 'Failed to create unified send job');
      }
    } catch (err) {
      console.error('Failed to create unified reply job:', err);
      toast.error('Error starting unified reply job');
    } finally {
      setSendingUnifiedReply(false);
    }
  };

  // Render nested replies
  const renderNestedReply = (reply, depth = 0) => {
    const isReplying = replyingToId === reply.id;
    const hasBatchSuggestion = batchSuggestions[reply.id];

    return (
      <div key={reply.id} className="ml-8">
        <div className={styles.replyCard}>
          <div className={styles.replyHeader}>
            <div className={styles.replyUser}>
              <strong>@{reply.username}</strong>
              {reply.replied_by_me && (
                <span className={styles.repliedBadge}>
                  Replied
                </span>
              )}
            </div>
            <span className={styles.replyTime}>
              {new Date(reply.timestamp).toLocaleDateString('en-US')}
            </span>
          </div>

          <div className={styles.replyText}>
            {reply.text}
          </div>

          {hasBatchSuggestion && (
            <div className={styles.aiSuggestion}>
              <div className={styles.aiSuggestionHeader}>
                AI Suggested Reply:
              </div>
              <TextArea
                value={batchSuggestions[reply.id]}
                onChange={(e) => handleSuggestionEdit(reply.id, e.target.value)}
                autoSize={{ minRows: 1, maxRows: 4 }}
                className="mt-1"
              />
            </div>
          )}

          {!reply.replied_by_me && reply.username !== currentUsername && (
            <div className={styles.replyActions}>
              <AntButton
                size="small"
                icon={<Icon icon="mdi:robot" width="16" />}
                onClick={() => handleAiSuggestion(reply)}
                loading={loadingAiSuggestion === reply.id}
              >
                AI Suggest
              </AntButton>
              <AntButton
                size="small"
                icon={<Icon icon="mdi:reply" width="16" />}
                onClick={() => {
                  setReplyingToId(reply.id);
                  setReplyText('');
                }}
              >
                Reply
              </AntButton>
            </div>
          )}

          {isReplying && (
            <div className={styles.replyInput}>
              <TextArea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Enter reply content..."
                rows={3}
                className="mb-2"
              />
              <div className="flex gap-2">
                <AntButton
                  type="primary"
                  size="small"
                  onClick={() => handleSendReply(reply.id)}
                  loading={sendingReply}
                >
                  Send
                </AntButton>
                <AntButton
                  size="small"
                  onClick={() => {
                    setReplyingToId(null);
                    setReplyText('');
                  }}
                >
                  Cancel
                </AntButton>
              </div>
            </div>
          )}

          <div className={styles.replyStats}>
            <span>
              <Icon icon="mdi:heart" width="14" />
              {reply.like_count || 0}
            </span>
          </div>
        </div>

        {reply.children && reply.children.length > 0 && (
          <div>
            {reply.children.map(child => renderNestedReply(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Empty states
  if (!selectedAccountId || accounts.length === 0) {
    return (
      <MainLayout activePage="posts-comments">
        <div className="flex-1 flex items-center justify-center">
          <div className={styles.emptyCard}>
            <div className={styles.emptyIcon}>
              <Icon icon="mdi:account-alert" />
            </div>
            <h3 className={styles.emptyTitle}>
              No Account Connected
            </h3>
            <p className={styles.emptyDescription}>
              Please connect a social media account to manage posts and comments
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
  if (selectedAccount && selectedAccount.platform !== 'threads') {
    return (
      <MainLayout activePage="posts-comments">
        <div className="flex-1 flex items-center justify-center">
          <div className={styles.emptyCard}>
            <div className={styles.emptyIcon}>
              <Icon icon="mdi:information" />
            </div>
            <h3 className={styles.emptyTitle}>
              Threads Only
            </h3>
            <p className={styles.emptyDescription}>
              Posts and comments management is currently only available for Threads accounts
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout activePage="posts-comments">
      <div className="flex-1 flex flex-col">
          {/* Header */}
          <div
            className="px-10 py-5 flex items-center justify-between"
            style={{ borderBottom: `1px solid ${theme.borderColor}` }}
          >
            <div>
              <h1 className="m-0 mb-2 text-[1.75rem] font-bold" style={{ color: theme.textPrimary }}>
                Posts & Comments
              </h1>
              <p className="m-0 text-sm" style={{ color: theme.textSecondary }}>
                Manage your posts and reply to comments
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Refresh Button */}
              <Button
                variant="secondary"
                icon="mdi:refresh"
                onClick={refreshPosts}
                loading={refreshing}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-10 overflow-y-auto">
            <div className={styles.container}>
              {/* Posts and Comments Section */}
              <div className={styles.postsCommentsSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.postsCommentsStats}>
                    <div className={styles.statItem}>
                      <Icon icon="mdi:post" width="18" className="text-slate-500" />
                      <span className={styles.statNumber}>{posts.length}</span>
                      <span className={styles.statLabel}>Posts</span>
                    </div>
                    <div className={styles.statDivider}></div>
                    <div className={styles.statItem}>
                      <Icon icon="mdi:comment" width="18" className="text-slate-500" />
                      <span className={styles.statNumber}>
                        {posts.reduce((sum, post) => sum + (post.replyCount || 0), 0)}
                      </span>
                      <span className={styles.statLabel}>Comments</span>
                    </div>
                  </div>
                </div>

                <div className={styles.postsCommentsContainer}>
                  {/* Posts List */}
                  <div className={styles.postsList}>
                    {loadingPosts ? (
                      <div className={styles.loadingPosts}>
                        <SkeletonListItem />
                        <SkeletonListItem />
                        <SkeletonListItem />
                      </div>
                    ) : posts.length === 0 ? (
                      <div className={styles.emptyPosts}>
                        <Icon icon="mdi:post-outline" className="text-[2rem] text-slate-300" />
                        <p>No posts yet</p>
                      </div>
                    ) : (
                      <>
                        {posts.map(post => (
                          <div
                            key={post.id}
                            className={`${styles.postCard} ${selectedPost?.id === post.id ? styles.postCardActive : ''}`}
                            onClick={() => handlePostClick(post)}
                          >
                            <div className={styles.postContent}>
                              {post.content}
                            </div>
                            <div className={styles.postStats}>
                              <span>
                                <Icon icon="mdi:heart" width="14" />
                                {post.likeCount ?? post.like_count ?? '-'}
                              </span>
                              <span>
                                <Icon icon="mdi:comment" width="14" />
                                {post.replyCount ?? post.reply_count ?? '-'}
                              </span>
                              <span>
                                <Icon icon="mdi:repeat" width="14" />
                                {post.repostCount ?? post.repost_count ?? '-'}
                              </span>
                            </div>
                          </div>
                        ))}

                        {/* Load More Posts Button */}
                        {allPosts.length > posts.length && (
                          <div className={styles.loadMore}>
                            <AntButton
                              onClick={loadAllPosts}
                              loading={loadingMorePosts}
                              icon={<Icon icon="mdi:download" width="16" />}
                            >
                              Load All Posts ({allPosts.length})
                            </AntButton>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Replies Section - Only show when post is selected */}
                  {selectedPost && (
                    <div className={styles.repliesSection}>
                      <div className={styles.repliesHeader}>
                        <div className="flex-1">
                          <h4 className={styles.repliesTitle}>
                            Comments ({allReplies.length})
                          </h4>
                          <div className="relative">
                            <div
                              className={`${styles.selectedPostContent} ${!isPostContentExpanded ? 'line-clamp-5' : ''}`}
                            >
                              {selectedPost.content}
                            </div>
                            {selectedPost.content && selectedPost.content.length > 200 && (
                              <div className="flex justify-center mt-2">
                                <button
                                  onClick={() => setIsPostContentExpanded(!isPostContentExpanded)}
                                  className="text-slate-400 hover:text-slate-600 bg-transparent border-none cursor-pointer p-0"
                                >
                                  <Icon icon={isPostContentExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} width="20" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedPost(null);
                            setIsPostContentExpanded(false);
                          }}
                          className={styles.closeButton}
                        >
                          <Icon icon="mdi:close" width="20" />
                        </button>
                      </div>

                      {/* Action Buttons */}
                      <div className={styles.actionButtons}>
                        <AntButton
                          type="primary"
                          icon={<Icon icon="mdi:robot-excited" width="16" />}
                          onClick={openBatchPromptModal}
                          loading={batchReplying}
                          disabled={batchSending}
                          size="small"
                        >
                          Batch AI Reply
                        </AntButton>

                        {batchReplying && (
                          <>
                            <AntButton
                              icon={<Icon icon={batchPaused ? "mdi:play" : "mdi:pause"} width="16" />}
                              onClick={handlePauseBatch}
                              size="small"
                            >
                              {batchPaused ? 'Resume' : 'Pause'}
                            </AntButton>
                            <AntButton
                              danger
                              icon={<Icon icon="mdi:stop" width="16" />}
                              onClick={handleCancelBatch}
                              size="small"
                            >
                              Cancel
                            </AntButton>
                          </>
                        )}

                        {Object.keys(batchSuggestions).length > 0 && (
                          <>
                            <AntButton
                              type="primary"
                              icon={<Icon icon="mdi:send" width="16" />}
                              onClick={handleBatchSend}
                              loading={batchSending}
                              disabled={batchReplying}
                              size="small"
                            >
                              Send All Replies ({Object.keys(batchSuggestions).length})
                            </AntButton>
                            <AntButton
                              danger
                              icon={<Icon icon="mdi:delete-sweep" width="16" />}
                              onClick={handleClearAllSuggestions}
                              disabled={batchReplying || batchSending}
                              size="small"
                            >
                              Clear All
                            </AntButton>
                          </>
                        )}

                        <AntButton
                          icon={<Icon icon="mdi:reply-all" width="16" />}
                          onClick={() => setShowUnifiedReplyModal(true)}
                          disabled={batchReplying || batchSending}
                          size="small"
                        >
                          Unified Reply
                        </AntButton>
                      </div>

                      {/* Progress Bar */}
                      {batchReplying && batchProgress.total > 0 && (
                        <div className={styles.progressContainer}>
                          <Progress
                            percent={batchProgress.percentage || Math.round((batchProgress.current / batchProgress.total) * 100)}
                            status={batchPaused ? 'exception' : 'active'}
                            size="small"
                          />
                          <div className={styles.progressText}>
                            {batchProgress.current} / {batchProgress.total} comments processed
                            {batchPaused && <span className="text-red-500 ml-2">(Paused)</span>}
                            {currentJobId && <span className="text-blue-500 ml-2">(Background)</span>}
                          </div>
                        </div>
                      )}

                      <div className={styles.repliesList}>
                        {loadingReplies ? (
                          <div className={styles.loadingReplies}>
                            <SkeletonListItem />
                            <SkeletonListItem />
                          </div>
                        ) : replies.length === 0 ? (
                          <div className={styles.emptyReplies}>
                            <Icon icon="mdi:comment-outline" className="text-[2rem] text-slate-300" />
                            <p>No comments yet</p>
                          </div>
                        ) : (
                          <>
                            {replies.map(reply => {
                              const isReplying = replyingToId === reply.id;
                              const hasBatchSuggestion = batchSuggestions[reply.id];

                              return (
                                <div key={reply.id} className={styles.replyCard}>
                                  <div className={styles.replyHeader}>
                                    <div className={styles.replyUser}>
                                      <strong>@{reply.username}</strong>
                                      {reply.replied_by_me && (
                                        <span className={styles.repliedBadge}>
                                          Replied
                                        </span>
                                      )}
                                    </div>
                                    <span className={styles.replyTime}>
                                      {new Date(reply.timestamp).toLocaleDateString('en-US')}
                                    </span>
                                  </div>

                                  <div className={styles.replyText}>
                                    {reply.text}
                                  </div>

                                  {hasBatchSuggestion && (
                                    <div className={styles.aiSuggestion}>
                                      <div className={styles.aiSuggestionHeader}>
                                        AI Suggested Reply:
                                      </div>
                                      <TextArea
                                        value={batchSuggestions[reply.id]}
                                        onChange={(e) => handleSuggestionEdit(reply.id, e.target.value)}
                                        autoSize={{ minRows: 1, maxRows: 4 }}
                                        className="mt-1"
                                      />
                                    </div>
                                  )}

                                  {!reply.replied_by_me && reply.username !== currentUsername && (
                                    <div className={styles.replyActions}>
                                      <AntButton
                                        size="small"
                                        icon={<Icon icon="mdi:robot" width="16" />}
                                        onClick={() => handleAiSuggestion(reply)}
                                        loading={loadingAiSuggestion === reply.id}
                                      >
                                        AI Suggest
                                      </AntButton>
                                      <AntButton
                                        size="small"
                                        icon={<Icon icon="mdi:reply" width="16" />}
                                        onClick={() => {
                                          setReplyingToId(reply.id);
                                          setReplyText('');
                                        }}
                                      >
                                        Reply
                                      </AntButton>
                                    </div>
                                  )}

                                  {isReplying && (
                                    <div className={styles.replyInput}>
                                      <TextArea
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        placeholder="Enter reply content..."
                                        rows={3}
                                        className="mb-2"
                                      />
                                      <div className="flex gap-2">
                                        <AntButton
                                          type="primary"
                                          size="small"
                                          onClick={() => handleSendReply(reply.id)}
                                          loading={sendingReply}
                                        >
                                          Send
                                        </AntButton>
                                        <AntButton
                                          size="small"
                                          onClick={() => {
                                            setReplyingToId(null);
                                            setReplyText('');
                                          }}
                                        >
                                          Cancel
                                        </AntButton>
                                      </div>
                                    </div>
                                  )}

                                  <div className={styles.replyStats}>
                                    <span>
                                      <Icon icon="mdi:heart" width="14" />
                                      {reply.like_count || 0}
                                    </span>
                                  </div>

                                  {/* Nested replies */}
                                  {reply.children && reply.children.length > 0 && (
                                    <div className={styles.nestedReplies}>
                                      {reply.children.map(child => renderNestedReply(child, 1))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {allReplies.length > displayedRepliesCount && (
                              <div className={styles.loadMore}>
                                <AntButton onClick={handleLoadAllReplies}>
                                  Load All {allReplies.length} Comments
                                </AntButton>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Unified Reply Modal */}
      <Modal
        title="Unified Reply"
        open={showUnifiedReplyModal}
        onCancel={() => {
          setShowUnifiedReplyModal(false);
          setUnifiedReplyFilter('');
        }}
        footer={null}
      >
        <div className="mb-4">
          <p className="text-slate-500 text-sm mb-3">
            This will send the same reply to all unreplied comments.
            <br />
            <span className="text-xs text-slate-400">
              Runs in background - you can close this modal and continue browsing.
            </span>
          </p>

          {/* Filter Input */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Filter by keyword (optional)
            </label>
            <Input
              value={unifiedReplyFilter}
              onChange={(e) => setUnifiedReplyFilter(e.target.value)}
              placeholder="Only reply to comments containing..."
              size="small"
            />
            <p className="text-xs text-slate-400 mt-1">
              Leave empty to reply to all unreplied comments
            </p>
          </div>

          <label className="block text-xs font-medium text-slate-600 mb-1">
            Reply content
          </label>
          <TextArea
            value={unifiedReplyText}
            onChange={(e) => setUnifiedReplyText(e.target.value)}
            placeholder="Enter unified reply content..."
            rows={4}
          />
        </div>

        <div className="flex gap-2 justify-end">
          <AntButton onClick={() => {
            setShowUnifiedReplyModal(false);
            setUnifiedReplyFilter('');
          }} disabled={sendingUnifiedReply}>
            Cancel
          </AntButton>
          <AntButton
            type="primary"
            onClick={handleUnifiedReply}
            loading={sendingUnifiedReply}
          >
            Send
          </AntButton>
        </div>
      </Modal>

      {/* Batch AI Prompt Modal */}
      <Modal
        title="Batch AI Reply Settings"
        open={showBatchPromptModal}
        onCancel={() => setShowBatchPromptModal(false)}
        footer={null}
      >
        <div className="mb-4">
          <p className="text-slate-500 text-sm mb-3">
            Customize how AI generates reply suggestions (optional)
          </p>
          <TextArea
            value={batchCustomPrompt}
            onChange={(e) => setBatchCustomPrompt(e.target.value)}
            placeholder="e.g., Reply with emojis, keep it under 5 words, use humor..."
            rows={4}
            className="mb-2"
          />
          <p className="text-xs text-slate-400">
            Leave empty to use default AI behavior
          </p>
        </div>

        <div className="flex gap-2 justify-end">
          <AntButton onClick={() => setShowBatchPromptModal(false)}>
            Cancel
          </AntButton>
          <AntButton
            type="primary"
            icon={<Icon icon="mdi:robot-excited" width="16" />}
            onClick={() => handleBatchAiReply(batchCustomPrompt)}
          >
            Start Batch AI Reply
          </AntButton>
        </div>
      </Modal>
    </MainLayout>
  );
}

export default PostsCommentsPage;
